import type { PricingCampaignModel } from '@/shared/generated'
import type { MouseEvent } from 'react'

import { Link } from '@tanstack/react-router'
import { formatDistanceToNow } from 'date-fns'
import { filter, map, pipe, prop, propEq, sum, values } from 'lodash/fp'
import {
	CopyIcon,
	EllipsisVerticalIcon,
	LayersIcon,
	Link2Icon,
	LinkIcon,
	PencilIcon,
	PlusIcon,
	PuzzleIcon,
	Trash2Icon,
} from 'lucide-react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { ApplyPricingCampaignTemplate } from '@/features/pricing-campaign/apply-template'
import { CopyPricingCampaignSettings } from '@/features/pricing-campaign/copy-settings'
import { CopyPricingCampaign } from '@/features/pricing-campaign/copy-to-og'
import { DeletePricingCampaign } from '@/features/pricing-campaign/delete'
import { DuplicatePricingCampaign } from '@/features/pricing-campaign/duplicate'
import { usePricingCampaignContext } from '@/features/pricing-campaign/edit'
import { ReassignPricingCampaign } from '@/features/pricing-campaign/reassign-to-og'
import { RenamePricingCampaign } from '@/features/pricing-campaign/rename'
import { SavePricingCampaignAsTemplate } from '@/features/pricing-campaign/save-as-template'
import { ShareLinkPricingCampaign } from '@/features/pricing-campaign/share'

import {
	createPricingCampaignLinkForProducts,
	EngineBadge,
	getAssortmentShare,
	getCountsByPc,
	GlobalPcBadge,
	isGlobalPricingCampaign,
	PricingCampaignPeriod,
	PricingCampaignPeriodMessage,
	useProductsCountsByPcQuery,
} from '@/entities/pricing-campaign'
import { UsernameById } from '@/entities/user'

import { PercentageValueText } from '@/shared/components/common/PercentageValueText'
import { Operators } from '@/shared/components/query-builder'
import { Button } from '@/shared/components/ui/button'
import {
	DescriptionDetail,
	DescriptionGroup,
	DescriptionList,
	DescriptionTerm,
} from '@/shared/components/ui/description-list'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu'
import { LinkButton } from '@/shared/components/ui/link-button'
import { ModalHeader, ModalTitle } from '@/shared/components/ui/modal'
import { Separator } from '@/shared/components/ui/separator'
import { Spinner } from '@/shared/components/ui/spinner'
import { ApplyAction } from '@/shared/generated'
import { ampli } from '@/shared/lib/ampli'
import { useModals } from '@/shared/lib/modal-manager'
import { cn } from '@/shared/lib/utils'

export function PricingCampaignFormHeader() {
	const context = usePricingCampaignContext()

	return (
		<ModalHeader className='pr-6'>
			<div className='flex w-full items-start justify-between'>
				<ModalTitle data-testid='pc-modal-title'>
					{context.methods.watch('name') || 'None'}
				</ModalTitle>
				<div className='flex items-center gap-2'>
					<div className='ml-auto flex shrink-0 items-center gap-2'>
						<PricingCampaignPeriodInformationMessage />
						<PricingCampaignActionsDropdown />
					</div>
					<Separator orientation='vertical' className='mr-2 h-6!' />
				</div>
			</div>
		</ModalHeader>
	)
}

type ProductsCountProps = {
	productsCount: number
	pricingCampaign: PricingCampaignModel
}
export function PricingCampaignFormInformation() {
	const { t } = useTranslation()

	const context = usePricingCampaignContext()
	const pricingCampaign = context?.pricingCampaign
	const engine = context.methods.watch('settings.pricing_tactics.engine')

	return (
		<DescriptionList className='my-4 flex items-start gap-10'>
			<DescriptionGroup>
				<DescriptionTerm>
					{t('engine', { defaultValue: 'Engine' })}
				</DescriptionTerm>
				<DescriptionDetail>
					<div className='inline-flex items-center space-x-1'>
						{!!engine && (
							<EngineBadge engine={engine}>
								{t(engine, { defaultValue: engine })}
							</EngineBadge>
						)}
						{!context.optimizationGroupId && <GlobalPcBadge />}
					</div>
				</DescriptionDetail>
			</DescriptionGroup>
			<DescriptionGroup>
				<DescriptionTerm>
					{t('last_modified_at', { defaultValue: 'Last modified' })}
				</DescriptionTerm>
				<DescriptionDetail>
					{pricingCampaign?.updated_at ? (
						<span className='font-medium'>
							{formatDistanceToNow(
								new Date(pricingCampaign?.updated_at + 'Z'),
								{
									addSuffix: true,
								}
							)}
							{pricingCampaign?.updated_at &&
								` ${t('by', { defaultValue: 'by' })} `}
							{pricingCampaign?.updated_by && (
								<UsernameById userId={pricingCampaign?.updated_by} />
							)}
						</span>
					) : (
						'-'
					)}
				</DescriptionDetail>
			</DescriptionGroup>
			<DescriptionGroup>
				<DescriptionTerm>
					{t('items_in_pc', { defaultValue: 'Items in PC' })}
				</DescriptionTerm>
				<DescriptionDetail>
					{pricingCampaign ? (
						<ProductsCount
							productsCount={context.productsCount}
							pricingCampaign={pricingCampaign}
						/>
					) : (
						context.productsCount || '-'
					)}
				</DescriptionDetail>
			</DescriptionGroup>
			{isGlobalPricingCampaign(pricingCampaign) && (
				<DescriptionGroup>
					<DescriptionTerm>
						{t('global.pc.presented_in', { defaultValue: 'Presented in OGs' })}
					</DescriptionTerm>
					<DescriptionDetail>
						<AffectInOptimizationGroups pricingCampaign={pricingCampaign} />
					</DescriptionDetail>
				</DescriptionGroup>
			)}
			<DescriptionGroup>
				<DescriptionTerm>
					{t('general_period', { defaultValue: 'Period' })}
				</DescriptionTerm>
				<DescriptionDetail>
					<CurrentPricingCampaignPeriod />
				</DescriptionDetail>
			</DescriptionGroup>
		</DescriptionList>
	)
}
function ProductsCount({
	productsCount = 0,
	pricingCampaign,
}: ProductsCountProps) {
	const handleProductsCountLinkClick = () =>
		ampli.settingsPricingCampaignsProductsCountClick()
	const { isFetching, data } = useProductsCountsByPcQuery<
		Record<string, number>
	>(
		{
			filters: isGlobalPricingCampaign(pricingCampaign)
				? []
				: [
						{
							name: 'optimization_group_id',
							operation: Operators.IS as const,
							value: pricingCampaign.optimization_group_id!,
						},
					],
		},
		{
			select: getCountsByPc,
			enabled: !!pricingCampaign?.optimization_group_id,
		}
	)
	const total = sum(values(data)) || 0
	const count = pricingCampaign?.id
		? data?.[pricingCampaign?.id]
		: productsCount || 0

	return (
		<LinkButton
			asChild
			disabled={!pricingCampaign?.id}
			className={cn('font-mono', {
				'pointer-events-none': !pricingCampaign?.id,
			})}
		>
			<Link
				onClick={handleProductsCountLinkClick}
				to={createPricingCampaignLinkForProducts(pricingCampaign)}
			>
				{isFetching ? (
					<Spinner />
				) : (
					<PercentageValueText
						value={productsCount}
						percentage={getAssortmentShare(total, count || 0)}
					/>
				)}
			</Link>
		</LinkButton>
	)
}
function AffectInOptimizationGroups({
	pricingCampaign,
}: {
	pricingCampaign?: PricingCampaignModel
}) {
	const { t } = useTranslation()

	const { showModal } = useModals()
	const { isFetching, data: optimizationGroupsIds } =
		useProductsCountsByPcQuery(
			{},
			{
				enabled: !!pricingCampaign?.id,
				select: pipe([
					filter(propEq('pricing_campaign_id', pricingCampaign?.id)),
					map(prop('optimization_group_id')),
				]),
				refetchOnMount: 'always',
			}
		)
	const handleShow = (e: MouseEvent<HTMLButtonElement>) => {
		e.preventDefault()
		if (!optimizationGroupsIds?.length) return

		showModal('AFFECTED_OPTIMIZATION_GROUPS_MODAL', {
			title:
				t('global.pc.presented_in.modal.title', {
					defaultValue: 'Presented in OGs',
				}) + ` (${optimizationGroupsIds?.length})`,
			subtitle: t('global.pc.presented_in.modal.subtitle'),
			getOptimizationGroupsIds: () => optimizationGroupsIds,
		})
	}

	return pricingCampaign?.id ? (
		isFetching ? (
			<Spinner />
		) : (
			<LinkButton onClick={handleShow}>
				{optimizationGroupsIds?.length}
			</LinkButton>
		)
	) : (
		<strong>0</strong>
	)
}
const CurrentPricingCampaignPeriod = () => {
	const { watch } = useFormContext()
	const startDate = watch('settings.activity.start_date')
	const endDate = watch('settings.activity.end_date')

	return <PricingCampaignPeriod startDate={startDate} endDate={endDate} />
}
export function PricingCampaignActionsDropdown() {
	const { t } = useTranslation()

	const { pricingCampaign, isGlobal, isSimulatedGlobal, isReadOnly } =
		usePricingCampaignContext()
	const pricingCampaignId = pricingCampaign?.id
	const { hideModal } = useModals()
	if (!pricingCampaignId) return null

	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				<Button
					data-testid='pc-actions-popover-trigger'
					variant='ghost'
					size='icon-sm'
				>
					<EllipsisVerticalIcon />
				</Button>
			</DropdownMenuTrigger>
			<DropdownMenuContent align='end'>
				<ApplyPricingCampaignTemplate
					action={ApplyAction.ReplacePricingCampaignSettings}
					pricingCampaignId={pricingCampaignId}
					onMutate={() => {
						hideModal('PRICING_CAMPAIGN_MODAL')
					}}
				>
					<DropdownMenuItem
						onClick={() => ampli.pcApplyTemplateClicked()}
						disabled={isReadOnly}
					>
						<PuzzleIcon /> {t('pc.action.apply_template')}
					</DropdownMenuItem>
				</ApplyPricingCampaignTemplate>
				<SavePricingCampaignAsTemplate pricingCampaignId={pricingCampaignId}>
					<DropdownMenuItem onClick={() => ampli.pcSaveAsTemplateClicked()}>
						<PlusIcon />
						{t('settings.action.save_as_template', {
							defaultValue: 'Save as template',
						})}
					</DropdownMenuItem>
				</SavePricingCampaignAsTemplate>
				<RenamePricingCampaign pricingCampaignId={pricingCampaignId}>
					<DropdownMenuItem disabled={isReadOnly}>
						<PencilIcon /> {t('app.edit_name', { defaultValue: 'Edit name' })}
					</DropdownMenuItem>
				</RenamePricingCampaign>
				<ShareLinkPricingCampaign pricingCampaignId={pricingCampaignId}>
					<DropdownMenuItem>
						<LinkIcon />{' '}
						{t('pc.action.copy_link', { defaultValue: 'Copy link' })}
					</DropdownMenuItem>
				</ShareLinkPricingCampaign>
				<ReassignPricingCampaign pricingCampaignsIds={[pricingCampaignId]}>
					<DropdownMenuItem
						disabled={isGlobal || isReadOnly}
						onClick={() => ampli.pcReassignClicked()}
					>
						<Link2Icon /> {t('pc.action.reassign')}
					</DropdownMenuItem>
				</ReassignPricingCampaign>
				<CopyPricingCampaignSettings pricingCampaignId={pricingCampaignId}>
					<DropdownMenuItem
						onClick={() => ampli.pcCopySettingsClicked()}
						disabled={isSimulatedGlobal}
					>
						<CopyIcon />
						{t('pc.action.copy_settings', {
							defaultValue: 'Copy settings to ...',
						})}
					</DropdownMenuItem>
				</CopyPricingCampaignSettings>
				<CopyPricingCampaign pricingCampaignsIds={[pricingCampaignId]}>
					<DropdownMenuItem
						disabled={isGlobal}
						onClick={() => ampli.pcCopyClicked()}
					>
						<CopyIcon /> {t('pc.action.copy', { defaultValue: 'Copy to ...' })}
					</DropdownMenuItem>
				</CopyPricingCampaign>
				<DuplicatePricingCampaign
					pricingCampaignId={pricingCampaignId}
					destinationIds={[pricingCampaign?.optimization_group_id ?? null]}
					destinationType='optimization_group'
				>
					<DropdownMenuItem
						disabled={isReadOnly || isSimulatedGlobal}
						onClick={() => ampli.pcDuplicateClicked()}
					>
						<LayersIcon /> {t('general_duplicate')}
					</DropdownMenuItem>
				</DuplicatePricingCampaign>
				<DeletePricingCampaign pricingCampaignId={pricingCampaignId}>
					<DropdownMenuItem
						disabled={isReadOnly}
						variant='destructive'
						onClick={() => ampli.pcDeleteClicked()}
					>
						<Trash2Icon /> {t('general_delete')}
					</DropdownMenuItem>
				</DeletePricingCampaign>
			</DropdownMenuContent>
		</DropdownMenu>
	)
}

export const PricingCampaignPeriodInformationMessage = () => {
	const { watch } = useFormContext()
	const startDate = watch('settings.activity.start_date')
	const endDate = watch('settings.activity.end_date')
	const isForceInactive = watch('settings.activity.force_inactive')

	return (
		<PricingCampaignPeriodMessage
			startDate={startDate}
			endDate={endDate}
			isForceInactive={isForceInactive}
		/>
	)
}
